/**
 * The PR pathway enquiry: a public form a prospective client fills in so the office can plan their
 * permanent residency journey. The form's fields mirror the backend's PR enquiry record one to one,
 * so the form state is also the request body (see buildPrEnquiryPayload).
 */

/** Where a share link's slug opens the public form, on this dashboard's own origin. */
export const buildPrEnquiryUrl = (slug, origin = window.location.origin) =>
  slug ? `${origin}/client/pr-enquiry/${encodeURIComponent(slug)}` : "";

export const VISA_TYPES = [
  "Student (500)",
  "Temporary Graduate (485)",
  "Working Holiday (417 / 462)",
  "Skilled Work Regional (491)",
  "Employer Sponsored (482 / 494)",
  "Partner (820 / 309)",
  "Visitor (600)",
  "Bridging visa",
  "Outside Australia / no visa yet",
  "Other",
];

export const COURSE_LEVELS = [
  "Certificate III / IV",
  "Diploma / Advanced Diploma",
  "Bachelor",
  "Graduate Certificate / Diploma",
  "Master",
  "PhD",
  "Not studying",
];

export const ENGLISH_TESTS = [
  "IELTS",
  "PTE Academic",
  "TOEFL iBT",
  "OET",
  "Cambridge (CAE)",
  "Not taken yet",
];

export const SKILLS_ASSESSMENT = [
  "Not started",
  "In progress",
  "Positive outcome",
  "Not sure I need one",
];

export const PR_PATHWAYS = [
  "Skilled Independent (189)",
  "Skilled Nominated (190)",
  "Skilled Work Regional (491)",
  "Skill in Demand (Subclass 482)",
  "Direct Entry stream (Subclass 186)",
  "Employer Sponsored (186 / 494)",
  "Temporary Graduate (485) first",
  "Partner visa",
  "Not sure, please advise me",
];

export const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT", "Any state"];

export const TIMELINES = ["Within 6 months", "6–12 months", "1–2 years", "More than 2 years"];

export const MARITAL_STATUSES = [
  "Single",
  "Married",
  "De facto",
  "Separated / Divorced",
  "Widowed",
];

export const YES_NO = ["Yes", "No"];

export const emptyExperience = () => ({
  role: "",
  employer: "",
  location: "",
  start: "",
  end: "",
  current: false,
  hoursPerWeek: "",
});

export const initialPrEnquiry = () => ({
  // About you
  name: "",
  email: "",
  number: "",
  dob: "",
  nationality: "",
  converted: false,
  maritalStatus: "",
  inAustralia: "",
  currentCity: "",
  // Visa & study
  visaStatus: "",
  visaExpiry: "",
  currentCourse: "",
  courseLevel: "",
  currentCourseProvider: "",
  courseEndDate: "",
  highestQualification: "",
  twoYearsAustralianStudy: "",
  regionalStudy: "",
  // Work
  onshore: [
    { role: "", employer: "", location: "", start: "", end: "", current: false, hoursPerWeek: "" },
  ],
  offshore: [
    { role: "", employer: "", location: "", start: "", end: "", current: false, hoursPerWeek: "" },
  ],
  // English & skills
  englishTest: "",
  englishScore: "",
  readingScore: "",
  listeningScore: "",
  speakingScore: "",
  writingScore: "",
  englishTestDate: "",
  occupation: "",
  skillsAssessment: "",
  professionalYear: "",
  naatiCcl: "",
  // Goals
  futurePlan: "",
  pathways: [],
  preferredStates: [],
  willingRegional: "",
  timeline: "",
  partnerIncluded: "",
  comments: "",
  consent: false,
});

export const PR_ENQUIRY_STEPS = [
  { id: "about", label: "About you" },
  { id: "visa", label: "Visa & study" },
  { id: "work", label: "Work experience" },
  { id: "skills", label: "English & skills" },
  { id: "goals", label: "Your PR goals" },
];

/** Whole months between two "YYYY-MM" values, counting both ends. */
export const monthsBetween = (start, end) => {
  const match = (value) => /^(\d{4})-(\d{2})$/.exec(value || "");
  const a = match(start);
  const b = match(end);
  if (!a || !b) return 0;
  const months = (Number(b[1]) - Number(a[1])) * 12 + (Number(b[2]) - Number(a[2])) + 1;
  return Math.max(months, 0);
};

const currentMonth = (today) => today.toISOString().slice(0, 7);

export const experienceMonths = (entry, today = new Date()) =>
  monthsBetween(entry.start, entry.current ? currentMonth(today) : entry.end);

export const formatDuration = (months) => {
  if (!months) return "0 months";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [];
  if (years) parts.push(`${years} yr${years === 1 ? "" : "s"}`);
  if (rest) parts.push(`${rest} mo`);
  return parts.join(" ");
};

export const totalExperience = (entries = [], today = new Date()) =>
  formatDuration(entries.reduce((sum, entry) => sum + experienceMonths(entry, today), 0));

/** One readable line per job, for the admin view: "Chef at ABC (Sydney) · 2023-02 → present · 38 h/week". */
export const describeExperience = (entry) => {
  const who = [entry.role, entry.employer && `at ${entry.employer}`].filter(Boolean).join(" ");
  const where = entry.location ? ` (${entry.location})` : "";
  const when = `${entry.start || "?"} → ${entry.current ? "present" : entry.end || "?"}`;
  const hours = entry.hoursPerWeek ? ` · ${entry.hoursPerWeek} h/week` : "";
  return `${who || "Role not given"}${where} · ${when}${hours}`;
};

const blank = (value) => !String(value ?? "").trim();

/** A job card nobody typed into, e.g. the one each list starts with. */
const isBlankJob = (entry) =>
  ["role", "employer", "location", "start", "end", "hoursPerWeek"].every((key) =>
    blank(entry?.[key])
  ) && !entry?.current;

export const filledJobs = (entries = []) => entries.filter((entry) => !isBlankJob(entry));

const experienceProblem = (entries, label) => {
  for (const [index, entry] of entries.entries()) {
    if (isBlankJob(entry)) continue;
    const n = `${label} job ${index + 1}`;
    if (blank(entry.role)) return `Add the role for ${n}.`;
    if (blank(entry.start)) return `Add when ${n} started.`;
    if (!entry.current && blank(entry.end))
      return `Add when ${n} ended, or tick "I still work here".`;
    if (!entry.current && entry.end < entry.start) return `${n} ends before it starts.`;
  }
  return null;
};

/** The first thing stopping this step from being completed, or null. */
export const prEnquiryStepProblem = (stepId, form) => {
  if (stepId === "about") {
    if (blank(form.name)) return "Please enter your full name.";
    if (blank(form.email) || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email.";
    if (blank(form.number)) return "Please enter your phone number.";
    if (blank(form.inAustralia)) return "Let us know whether you are in Australia now.";
  }
  if (stepId === "visa") {
    if (blank(form.visaStatus)) return "Please choose your current visa.";
    if (form.inAustralia === "Yes" && blank(form.visaExpiry)) {
      return "Please tell us when your visa is valid until.";
    }
  }
  if (stepId === "work") {
    return (
      experienceProblem(form.onshore, "Australian") || experienceProblem(form.offshore, "Overseas")
    );
  }
  if (stepId === "goals") {
    if (!form.pathways.length) return "Pick at least one pathway, or “Not sure, please advise me”.";
    if (!form.consent) return "Please agree to be contacted about your enquiry.";
  }
  return null;
};

const orNull = (value) => (blank(value) ? null : String(value).trim());

const FORM_FIELDS = Object.keys(initialPrEnquiry());

/**
 * The body the PR enquiry endpoints bind: every form field under its own name and nothing else,
 * so a stale draft cannot smuggle extra keys in. Untouched job cards are dropped, and a blank date
 * of birth goes as null because the backend reads it as a date. Other text goes as typed; the
 * backend trims it and stores blanks as absent.
 */
export const buildPrEnquiryPayload = (form) => ({
  ...Object.fromEntries(FORM_FIELDS.map((key) => [key, form[key]])),
  dob: orNull(form.dob),
  onshore: filledJobs(form.onshore),
  offshore: filledJobs(form.offshore),
});
